import { useState } from "react";
import { AlertCircle, Loader2, Plus, RefreshCw, UserCheck, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { getInitials, getAvatarColor } from "@/lib/avatar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import type { TesterUser } from "@/services/testers";

interface UserPickerProps {
  onSelect: (user: TesterUser) => void;
  cognitoUsers: TesterUser[];
  mode: "add" | "replace" | "assign-all";
  unresolvedCount?: number;
  excludeUserIds?: string[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  triggerClassName?: string;
}

export function UserPicker({
  onSelect,
  cognitoUsers,
  mode,
  unresolvedCount = 0,
  excludeUserIds = [],
  isLoading,
  isError,
  onRetry,
  triggerClassName,
}: UserPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const available = mode === "add"
    ? cognitoUsers.filter(u => !excludeUserIds.includes(u.id))
    : cognitoUsers;

  const filtered = mode === "assign-all" && search
    ? available.filter(u =>
        u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase())
      )
    : available;

  const handleSelect = (user: TesterUser) => {
    onSelect(user);
    setOpen(false);
    setSearch("");
  };

  const isCompact = mode === "add" || mode === "replace";

  const renderTrigger = () => {
    if (isLoading) {
      const icon = <Loader2 className={cn("animate-spin", isCompact ? "h-3 w-3" : "h-4 w-4")} />;
      if (isCompact) return icon;
      return <>{icon}Loading...</>;
    }
    if (isError) {
      const icon = <AlertCircle className={cn("text-destructive", isCompact ? "h-3 w-3" : "h-4 w-4")} />;
      if (isCompact) return icon;
      return <>{icon}Error</>;
    }

    switch (mode) {
      case "add":
        return <Plus className="h-3.5 w-3.5" />;
      case "replace":
        return <Users className="h-3 w-3" />;
      case "assign-all":
        return <><UserCheck className="h-4 w-4" />Assign all ({unresolvedCount} unresolved)</>;
    }
  };

  const triggerClasses = (() => {
    switch (mode) {
      case "add":
        return cn("h-6 w-6 p-0 rounded-full", triggerClassName);
      case "replace":
        return cn("h-5 w-5 p-0 rounded-full", triggerClassName);
      case "assign-all":
        return cn("gap-2 shrink-0 whitespace-nowrap", triggerClassName);
    }
  })();

  const popoverWidth = mode === "assign-all" ? "w-[300px]" : "w-[280px]";
  const popoverAlign = mode === "assign-all" ? "end" as const : "start" as const;

  const renderErrorState = () => (
    <div className="flex flex-col items-center gap-2 p-4 text-center">
      <AlertCircle className="h-6 w-6 text-destructive" />
      <p className="text-sm text-muted-foreground">Failed to load users</p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry} className="gap-1">
          <RefreshCw className="h-3 w-3" />
          Retry
        </Button>
      )}
    </div>
  );

  const renderLoadingState = () => (
    <div className="flex items-center justify-center gap-2 p-4 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      Loading users...
    </div>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={triggerClasses}>
          {renderTrigger()}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn(popoverWidth, "p-0")} align={popoverAlign}>
        {isLoading ? renderLoadingState() : isError ? renderErrorState() : (
          <Command>
            {mode === "assign-all" && (
              <div className="p-2 border-b border-border">
                <p className="text-xs text-muted-foreground px-1 pb-2">
                  Assign this user to all <strong>{unresolvedCount}</strong> unresolved tester slot(s).
                  Already-resolved slots are not changed.
                </p>
                <CommandInput
                  placeholder="Search user..."
                  value={search}
                  onValueChange={setSearch}
                />
              </div>
            )}
            {mode !== "assign-all" && (
              <CommandInput placeholder={mode === "add" ? "Search Cognito users..." : "Search users..."} />
            )}
            <CommandList>
              <CommandEmpty>No matching users.</CommandEmpty>
              <CommandGroup>
                {filtered.map((user) => (
                  <CommandItem
                    key={user.id}
                    value={`${user.name} ${user.email}`}
                    onSelect={() => handleSelect(user)}
                  >
                    <Avatar className="mr-2 h-5 w-5">
                      <AvatarFallback className={cn("text-[10px]", getAvatarColor(user.name))}>
                        {getInitials(user.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col min-w-0">
                      <span className="truncate text-sm">{user.name}</span>
                      <span className="truncate text-xs text-muted-foreground">{user.email}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}
